import type { ComputeRouteResponse } from '../types/canvasser'

/**
 * [A3 §5] The frozen walk order (and the last road line through it), per turf + travel mode, so a
 * reload — online or offline — never reshuffles. sessionStorage: tab-session lifetime, like the chosen
 * turf; cleared by closing the tab or ⚡ (`clearWalkOrder`). Any storage failure (private mode, quota)
 * just means "no persistence", never a crash.
 */
export interface StoredWalkOrder {
  kind: 'optimized' | 'approximate'
  order: string[]
  route: ComputeRouteResponse | null
}

type Mode = 'walking' | 'driving'

/** The next leg was computed from the GPS fix at the time to whichever stop was first then; served
 *  later (after a reload) it would draw a line to a door that may be done. Never store or serve it. */
export function withoutNextLeg(route: ComputeRouteResponse): ComputeRouteResponse {
  return { ...route, nextSegmentGeoJson: null, nextSegmentDistanceMeters: null, nextSegmentDurationSeconds: null }
}
const key = (turfId: string, mode: Mode) => `canvasser_walk_order_${turfId}_${mode}`

export function readWalkOrder(turfId: string, mode: Mode): StoredWalkOrder | null {
  try {
    const raw = sessionStorage.getItem(key(turfId, mode))
    if (!raw) return null
    const v = JSON.parse(raw) as Partial<StoredWalkOrder> | null
    if (!v || (v.kind !== 'optimized' && v.kind !== 'approximate')) return null
    if (!Array.isArray(v.order) || !v.order.every(k => typeof k === 'string')) return null
    const route = v.route != null && typeof v.route === 'object' && Array.isArray(v.route.waypointOrder)
      ? withoutNextLeg(v.route) : null
    return { kind: v.kind, order: v.order, route }
  } catch {
    return null
  }
}

export function writeWalkOrder(turfId: string, mode: Mode, v: StoredWalkOrder): void {
  const stored: StoredWalkOrder = { ...v, route: v.route ? withoutNextLeg(v.route) : null }
  try { sessionStorage.setItem(key(turfId, mode), JSON.stringify(stored)) } catch { /* no persistence */ }
}

export function clearWalkOrder(turfId: string, mode: Mode): void {
  try { sessionStorage.removeItem(key(turfId, mode)) } catch { /* ignore */ }
}
